import type { ColorAttribute } from './helpers';
import { normalizeAttributes, readAttributes } from './variantAttributes';
import { buildReplacementCandidates, getEditableAttributes } from './variantMappingRepairCandidates';

import { replacementKey, selectedMappingsStrategy } from './variantMappingRepairStrategies/manual';
import { renamedValuesStrategy } from './variantMappingRepairStrategies/renamedValues';
import { buildValueReplacementPlan, type ValueReplacementConflict } from './variantMappingValuePlan';
import { casingStrategy } from './variantMappingRepairStrategies/casing';
import { shopifyOptionOrderStrategy, shopifyOptionPositionsStrategy } from './variantMappingRepairStrategies/shopify';
import type { AttributeReplacement, RepairContext, RepairStrategyID, StoreOption, VariantMappingRepairStrategy } from './variantMappingRepairStrategies/types';

// Dependency order: resolve option identity before values; casing canonicalizes the final labels.
const repairStrategies: readonly VariantMappingRepairStrategy[] = [
    shopifyOptionOrderStrategy,
    shopifyOptionPositionsStrategy,
    renamedValuesStrategy,
    casingStrategy
];

// Merchant choices refer to original IDs, so apply them before any automatic transformation.
const getRepairStrategies = (context: RepairContext, replacements: readonly AttributeReplacement[] = []) =>
    [selectedMappingsStrategy(replacements), ...repairStrategies].filter((strategy) => strategy.isApplicable(context));

export const getApplicableRepairStrategyIDs = (context: RepairContext, replacements: readonly AttributeReplacement[] = []) =>
    getRepairStrategies(context, replacements).map((strategy) => strategy.id);

export type RepairStep = { strategyID: RepairStrategyID; before: ColorAttribute[]; after: ColorAttribute[] };

export type RepairEntry = { key: string; variantGuid: string; name: string; code: string };
export type VariantMappingRepair = RepairEntry & {
    before: ColorAttribute[];
    after: ColorAttribute[] | null;
    reason: 'unmatched' | 'duplicate' | 'value-conflict' | 'invalid' | null;
    steps: RepairStep[];
    editableAttributes: ColorAttribute[];
    valueConflicts?: ValueReplacementConflict[];
};
export type ChannelMappingRepair = {
    salesChannelID: string;
    label: string;
    isMain: boolean;
    productSKU: string;
    context: RepairContext;
    entries: RepairEntry[];
    replacements?: readonly AttributeReplacement[];
    repairs: VariantMappingRepair[];
    availableStrategies: RepairStrategyID[];
};

type ParsedEntry = { entry: RepairEntry; attributes: ColorAttribute[] | null };
type StrategyResult = { after: ColorAttribute[] | null; steps: RepairStep[] };

const hasUniqueOptions = (attributes: readonly ColorAttribute[]) => new Set(attributes.map((attribute) => attribute.Id)).size === attributes.length;
const sameAttributes = (before: readonly ColorAttribute[], after: readonly ColorAttribute[]) => JSON.stringify(before) === JSON.stringify(after);
const combinationKey = (attributes: readonly ColorAttribute[]) =>
    JSON.stringify(attributes.map((attribute) => [attribute.Id, attribute.Value.Id]).sort(([a], [b]) => a.localeCompare(b)));

const matchesStore = (attributes: readonly ColorAttribute[], options: readonly StoreOption[]) =>
    hasUniqueOptions(attributes) &&
    attributes.every((attribute) => {
        const matches = options.filter((option) => option.optionID === attribute.Id && option.label === attribute.Label);
        return matches.length === 1 && matches[0].values.some((value) => value.valueID === attribute.Value.Id && value.label === attribute.Value.Label);
    });

/** Revalidate at the apply boundary: a failed/invalid plan must never become a saved null mapping. */
export function getApplicableVariantMappingRepairs(repairs: readonly VariantMappingRepair[]): (VariantMappingRepair & { after: ColorAttribute[] })[] {
    return repairs.flatMap((repair) => {
        if (repair.reason !== null) return [];
        const after = normalizeAttributes(repair.after);
        return after?.length && hasUniqueOptions(after) ? [{ ...repair, after }] : [];
    });
}

const runStrategies = (attributes: ColorAttribute[], context: RepairContext, strategies: readonly VariantMappingRepairStrategy[]): StrategyResult =>
    strategies.reduce<StrategyResult>(
        (result, strategy) => {
            if (result.after === null) return result;
            const after = strategy.repair(result.after, context);
            if (after === null) return { after: null, steps: [] };
            return {
                after,
                steps: sameAttributes(result.after, after) ? result.steps : [...result.steps, { strategyID: strategy.id, before: result.after, after }]
            };
        },
        { after: attributes, steps: [] }
    );

const buildEntryRepair = (
    { entry, attributes }: ParsedEntry,
    context: RepairContext,
    strategies: readonly VariantMappingRepairStrategy[],
    valuePlan: ReturnType<typeof buildValueReplacementPlan>
): VariantMappingRepair[] => {
    if (!entry.variantGuid) return [];
    if (attributes === null) return [{ ...entry, before: [], after: null, reason: 'invalid', steps: [], editableAttributes: [] }];
    if (!attributes.length || matchesStore(attributes, context.options)) return [];
    const editableAttributes = getEditableAttributes(attributes, context, valuePlan.conflicts);
    if (attributes.some((attribute) => valuePlan.conflicts.has(replacementKey(attribute.Id, attribute.Value.Id))))
        return [
            {
                ...entry,
                before: attributes,
                after: null,
                reason: 'value-conflict',
                steps: [],
                editableAttributes,
                valueConflicts: attributes.flatMap((attribute) => {
                    const conflict = valuePlan.conflictDetails.get(replacementKey(attribute.Id, attribute.Value.Id));
                    return conflict ? [conflict] : [];
                })
            }
        ];
    const result = runStrategies(attributes, context, strategies);
    if (!result.after?.length || !matchesStore(result.after, context.options))
        return [{ ...entry, before: attributes, after: null, reason: 'unmatched', steps: [], editableAttributes }];
    return sameAttributes(attributes, result.after) ? [] : [{ ...entry, before: attributes, ...result, reason: null, editableAttributes }];
};

const rejectDuplicatePass = (entries: readonly ParsedEntry[], repairs: readonly VariantMappingRepair[]): VariantMappingRepair[] => {
    const repairsByKey = new Map(repairs.map((repair) => [repair.key, repair]));
    const combinations = entries
        .flatMap(({ entry, attributes }) => {
            const proposed = repairsByKey.get(entry.key)?.after ?? attributes;
            return proposed?.length ? [combinationKey(proposed)] : [];
        })
        .sort();
    const duplicates = new Set(combinations.filter((key, index) => index > 0 && key === combinations[index - 1]));
    return repairs.map((repair) =>
        repair.after && duplicates.has(combinationKey(repair.after))
            ? { ...repair, after: null, reason: 'duplicate', steps: [], editableAttributes: repair.before }
            : repair
    );
};

export const hasVariantMappingRepairCollisions = (entries: readonly RepairEntry[], repairs: readonly VariantMappingRepair[]) => {
    const parsed = entries.map((entry) => ({ entry, attributes: readAttributes(entry.code) }));
    return rejectDuplicatePass(parsed, repairs).some((repair, index) => repair !== repairs[index]);
};

const rejectDuplicateRepairs = (entries: readonly ParsedEntry[], repairs: VariantMappingRepair[]): VariantMappingRepair[] => {
    // Rejected repairs restore their original combination, which can expose further collisions.
    // Each pass returns new rejected entries; retain iteration here to avoid recursive stack growth.
    let current = repairs;
    while (true) {
        const next = rejectDuplicatePass(entries, current);
        if (next.every((repair, index) => repair === current[index])) return current;
        current = next;
    }
};

/** Parse, compose applicable strategies, then reject collisions without mutating inputs or repair results. */
export function buildVariantMappingRepairs(
    entries: readonly RepairEntry[],
    options: readonly StoreOption[],
    isShopify: boolean,
    replacements: readonly AttributeReplacement[] = []
): VariantMappingRepair[] {
    const context = { options, isShopify };
    const strategies = getRepairStrategies(context, replacements);
    const parsed = entries.map((entry) => ({ entry, attributes: readAttributes(entry.code) }));
    const valuePlan = buildValueReplacementPlan(
        parsed.flatMap(({ attributes }) => attributes ?? []),
        context,
        replacements
    );
    const repairs = parsed.flatMap((entry) => buildEntryRepair(entry, context, strategies, valuePlan));
    return rejectDuplicateRepairs(parsed, repairs);
}

/** The dropdown uses the same channel-wide plan as the repair pipeline, never isolated value guesses. */
export const getChannelValueReplacementPlan = (channel: ChannelMappingRepair, replacements: readonly AttributeReplacement[] = []) => {
    const valuePlan = buildValueReplacementPlan(
        channel.entries.flatMap((entry) => readAttributes(entry.code) ?? []),
        channel.context,
        replacements
    );
    return { ...valuePlan, candidates: buildReplacementCandidates(channel.repairs, channel.context, valuePlan, replacements) };
};

/** Replan from every original mapping so manual choices cannot collide with an unchanged variant. */
export const selectChannelMappingReplacements = (channel: ChannelMappingRepair, replacements: readonly AttributeReplacement[]): ChannelMappingRepair => ({
    ...channel,
    replacements,
    availableStrategies: getApplicableRepairStrategyIDs(channel.context, replacements),
    repairs: buildVariantMappingRepairs(channel.entries, channel.context.options, channel.context.isShopify, replacements)
});
